// The Settings sub-router (Design §14).
//
// **Every administrative screen is here**, under `/Settings/Admin/*`, rendered by `Settings.svelte`
// inside its own layout and reached from its second-level sidebar. The overview and the audit log
// always worked that way; the rest of administration sat at a top-level `/Admin/*` with a tab strip
// of its own, so the store had two administrative surfaces that looked nothing alike and neither
// listed the other's pages.
//
// The keys are **whole paths**, not fragments: the nested router matches against the location, and
// the outer table sends everything under `/Settings/*` here. A route added here and left out of
// `Settings.svelte`'s sidebar renders perfectly and can only be reached by typing its URL — which
// is what `AdminNavigationAgreement.test.ts` fails on.
import Account from './Account.svelte';
import AdminLogs from './AdminLogs.svelte';


// The administration landing page (Design §4.5), and the readiness gate with it: `Store setup` was
// a page of its own until it merged in here. It reads the same endpoint checkout consults, so it
// stays useful long after the first run rather than being a one-time wizard — which is why the
// overview merged into *it* rather than the other way round.
import AdminOverview from '../admin/Overview.svelte';
import AdminSettings from '../admin/Settings.svelte';
import AdminProviders from '../admin/Providers.svelte';
import AdminCurrencies from '../admin/Currencies.svelte';
import AdminTaxRates from '../admin/TaxRates.svelte';
import AdminRefundPolicy from '../admin/RefundPolicy.svelte';
import AdminSellers from '../admin/Sellers.svelte';
// Email (Design §11). The sender and the copy, both admin actions rather than deployments.
import AdminEmail from '../admin/Email.svelte';
// Operations (Design §13, §16). Nothing on this page blocks checkout, which is precisely why it
// exists: the inline reconcile keeps purchases completing with every worker stopped.
import AdminOperations from '../admin/Operations.svelte';
// Analytics (Design §12). A separate page from Operations because they answer different questions:
// Operations is read during an incident, this is read on a Monday morning.
import AdminAnalytics from '../admin/Analytics.svelte';
// Financial reports (Design §13). Beside Analytics rather than inside it, and the split is by
// source as much as by question: analytics reads the visit log and answers "what did people do",
// this reads invoices, orders and refunds and answers "what did the store take".
import AdminReports from '../admin/Reports.svelte';
// The invoice register (Design §4.6). Deliberately not the Reports page's document list, which
// reads the ledger and so sees only invoices that were **paid** — a draft, one awaiting payment,
// one voided or written off appeared on no admin screen at all, and "what is still owing" was
// unanswerable. Same filter bar, plus the invoice's own status.
import AdminInvoices from '../admin/Invoices.svelte';
import AdminCatalog from '../admin/Catalog.svelte';
import AdminApplications from '../admin/Applications.svelte';
// §13. Migrating the legacy store: an upload, the mapping panels, a dry run and an apply. Its own
// page because it is a procedure run once rather than a screen anybody visits twice.
import AdminMigration from '../admin/Migration.svelte';
// Support (Design §9.6, §13). A customer is addressed by the **user's** uuid — one customer is one
// user, so it has no external identifier of its own.
import AdminCustomers from '../admin/Customers.svelte';
import AdminCustomer from '../admin/Customer.svelte';
import AdminSales from '../admin/Sales.svelte';
import AdminSaleSubscription from '../admin/sales/Subscription.svelte';
import AdminSaleOrder from '../admin/sales/Order.svelte';

export default {
	'/Settings': Account,
	'/Settings/Account': Account,

	'/Settings/Admin': AdminOverview,
	'/Settings/Admin/Catalog': AdminCatalog,
	'/Settings/Admin/Settings': AdminSettings,
	'/Settings/Admin/Settings/Providers': AdminProviders,
	'/Settings/Admin/Settings/Currencies': AdminCurrencies,
	'/Settings/Admin/Settings/Tax': AdminTaxRates,
	'/Settings/Admin/Settings/Refunds': AdminRefundPolicy,
	'/Settings/Admin/Settings/Sellers': AdminSellers,
	'/Settings/Admin/Settings/Email': AdminEmail,
	'/Settings/Admin/Applications': AdminApplications,
	'/Settings/Admin/Migration': AdminMigration,
	'/Settings/Admin/Customers': AdminCustomers,
	'/Settings/Admin/Customers/:uuid': AdminCustomer,
	'/Settings/Admin/Transactions': AdminSales,
	'/Settings/Admin/Transactions/Subscription/:uuid': AdminSaleSubscription,
	'/Settings/Admin/Transactions/Order/:uuid': AdminSaleOrder,
	'/Settings/Admin/SalesFunnel': AdminAnalytics,
	'/Settings/Admin/Reports': AdminReports,
	'/Settings/Admin/Invoices': AdminInvoices,
	'/Settings/Admin/Operations': AdminOperations,
	'/Settings/Admin/Logs': AdminLogs
};
